/**
 * Navbar Component
 * Top navigation bar with user menu and notifications
 */

import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getTasks } from "../../services/tasks.service";
import { fetchActiveAlerts } from "../../services/alerts.service";
import { APP_NAME, QUERY_KEYS } from "../../utils/constants";
import { getInitials, formatDate } from "../../utils/formatters";
import {
  TaskPriority,
  TaskStatus,
  taskPriorityLabel,
} from "../../types/task.types";
import { Bars3Icon } from "@heroicons/react/24/outline";

function alertTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const Navbar: React.FC<{ onMenuClick?: () => void }> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationsRef = useRef<HTMLDivElement>(null);

  // Fetch assigned tasks for the current user
  const { data: assignedTasksData } = useQuery({
    queryKey: [QUERY_KEYS.TASKS, "assigned", user?.id],
    queryFn: () =>
      getTasks(
        { page: 1, limit: 10, sortBy: "due_date", sortOrder: "asc" },
        {
          status: [TaskStatus.TODO, TaskStatus.IN_PROGRESS],
          assigned_to: user?.id,
        },
      ),
    enabled: !!user,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const isSuperAdmin =
    user?.role === "superadmin" || user?.role === "super_admin";

  // Fetch active system alerts (superadmin only — RLS enforces this server-side too)
  const { data: activeAlerts } = useQuery({
    queryKey: [QUERY_KEYS.SYSTEM_ALERTS, "active"],
    queryFn: fetchActiveAlerts,
    enabled: !!user && isSuperAdmin,
    refetchInterval: 5 * 60 * 1000, // 5 minutes
  });

  const assignedTasks = assignedTasksData?.data || [];
  const taskCount = assignedTasks.length;
  const alertCount = activeAlerts?.length || 0;
  const totalCount = taskCount + alertCount;

  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      // Force immediate redirect to login page
      // This prevents getting stuck in loading state
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails, redirect to login
      window.location.href = "/login";
    }
  };

  return (
    <nav className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Left: mobile menu button + logo/app name */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {onMenuClick && (
            <button
              type="button"
              onClick={() => {
                // Close any open navbar dropdown first so a z-50 menu can't sit
                // above the z-40 drawer.
                setShowNotifications(false);
                setShowUserMenu(false);
                onMenuClick();
              }}
              className="md:hidden p-2 -ml-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label="Open menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          )}
          <img
            src="/logowhite.png"
            alt="U.S. LawShield Logo"
            className="h-10 w-auto shrink-0"
          />
          <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">
            {APP_NAME}
          </h1>
        </div>

        {/* Right side - User menu */}
        <div className="flex items-center space-x-4">
          {/* Notifications - Tasks + Alerts */}
          <div className="relative" ref={notificationsRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 text-gray-400 hover:text-gray-600 relative focus:outline-none"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              {totalCount > 0 && (
                <span className="absolute top-1 right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {totalCount > 9 ? "9+" : totalCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-sm bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-[32rem] overflow-y-auto">
                {/* Tasks Section */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Tasks ({taskCount})
                  </h3>
                </div>
                {assignedTasks.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {assignedTasks.slice(0, 5).map((task) => (
                      <button
                        key={task.id}
                        onClick={() => {
                          navigate("/tasks");
                          setShowNotifications(false);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {task.title}
                            </p>
                            {task.description && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                            <div className="flex items-center mt-2 space-x-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  // tasks.priority is an INTEGER 1-5 (5 = urgent)
                                  task.priority >= TaskPriority.URGENT
                                    ? "bg-red-100 text-red-800"
                                    : task.priority === TaskPriority.HIGH
                                      ? "bg-orange-100 text-orange-800"
                                      : task.priority === TaskPriority.MEDIUM
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {taskPriorityLabel(task.priority)}
                              </span>
                              {task.due_date && (
                                <span className="text-xs text-gray-500">
                                  Due: {formatDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center text-sm text-gray-500">
                    No assigned tasks
                  </div>
                )}
                <div className="px-4 py-2 border-b border-gray-200">
                  <button
                    onClick={() => {
                      navigate("/tasks");
                      setShowNotifications(false);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View all tasks →
                  </button>
                </div>

                {/* System Alerts Section */}
                <div className="px-4 py-3 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900">
                    System Alerts ({alertCount})
                  </h3>
                </div>
                {alertCount > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {activeAlerts!.slice(0, 5).map((alert) => (
                      <button
                        key={alert.id}
                        onClick={() => {
                          navigate("/error-logs");
                          setShowNotifications(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-l-4 ${
                          alert.severity === "critical"
                            ? "border-red-500"
                            : "border-yellow-500"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  alert.severity === "critical"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {alert.severity}
                              </span>
                              {alert.status === "acknowledged" && (
                                <span className="text-xs text-gray-400">
                                  ack'd
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-900 mt-1 truncate">
                              {alert.message}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {alertTimeAgo(alert.created_at)}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-4 text-center text-sm text-gray-500">
                    No active alerts
                  </div>
                )}
                <div className="px-4 py-2">
                  <button
                    onClick={() => {
                      navigate("/error-logs");
                      setShowNotifications(false);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    View alerts →
                  </button>
                </div>

                {/* Empty state when nothing */}
                {taskCount === 0 && alertCount === 0 && (
                  <div className="px-4 py-4 text-center text-sm text-gray-400">
                    No notifications
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 focus:outline-none"
            >
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary-600 text-white font-semibold">
                {user ? getInitials(user.full_name) : "U"}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-gray-900">
                  {user ? user.full_name : "User"}
                </p>
                <p className="text-xs text-gray-500">{user?.role}</p>
              </div>
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown menu */}
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50 border border-gray-200">
                <button
                  onClick={() => {
                    navigate("/settings");
                    setShowUserMenu(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Settings
                </button>
                <hr className="my-1" />
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
